import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ModalController } from "@ionic/angular";
import { TranslateService } from '@ngx-translate/core';
import { ColumnMode } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { EnumStringConverter } from 'src/components/shared-components/enum/enum-string-converter';
import { StaticClass } from 'src/components/shared-components/class/general-classes/static-classes';
import { AddressFormat, addressOperation } from 'src/components/shared-components/enum/enumAddress';
import { ConnectionType } from 'src/components/shared-components/enum/enumSettings';
import { iEvent } from 'src/components/shared-components/interfaces/iEvent';
import { iTranslation } from 'src/components/shared-components/interfaces/iTranslation';
import { EventService } from 'src/service/event.service';
import { NetworkService } from 'src/service/network.service';

@Component({
    selector: 'app-duplicate-object-popup',
    templateUrl: './duplicate-object-popup.component.html',
    styleUrls: ['./duplicate-object-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DuplicateObjectPopupComponent implements OnInit, OnDestroy, iEvent, iTranslation {
  private _networkServiceSubscription: Subscription = null;

  //#region translate
  private _translateSubscription: Subscription = null;
  public addressType: string = "Tipo indirizzo";
  public maingroup: string = "Tipo di modifica su maingroup";
  public mainValue: string = "Parametro di modifica";
  public middlegroup: string = "Tipo di modifica su middlegroup";
  public subgroup: string = "Tipo di modifica su subgroup";
  public afString: string = "Formato stringa (senza separatori '/')";
  public afKNXTwoLevels: string = "Formato KNX a 2 livelli MainGrp/SubGrp";
  public afKNXThreeLevels: string = "Formato KNX a 3 livelli MainGrp/MiddleGrp/SubGrp";
  public afInteger: string = "Formato KNX indirizzamento libero";
  public afStringIntegerInteger: string = "Formato stringa/numero/numero";
  public afStringStringInteger: string = "Formato stringa/stringa/numero";
  public afStringStringString: string = "Formato stringa/stringa/stringa";
  public afIntegerStringString: string = "Formato numero/stringa/stringa";
  public afIntegerIntegerString: string = "Formato numero/numero/stringa";
  public afIntegerStringInteger: string = "Formato numbero/stringa/numero";
  public afStringIntegerString: string = "Formato stringa/numero/stringa";
  public afStringString: string = "Formato stringa/stringa";
  public afIntegerString: string = "Formato numero/stringa";
  public afStringInteger: string = "Formato stringa/numero";
  //#endregion

  public _eventSubscriptions: Array<Subscription> = null;

  public rows = [];

  public ColumnMode = ColumnMode;
  public toolBarColor: string = "primary";
  public connectionType = ConnectionType;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;

  public duplicateForm: UntypedFormGroup;
  public addressOperation = addressOperation;
  constructor(
    private _formBuilder: UntypedFormBuilder,
    private _modalCtrl: ModalController,
    private _translateService: TranslateService,
    private _networkService: NetworkService,
    private _eventService: EventService
  ) {
    this.initializeRows();
    this.initializeForm();
  }

  ngOnInit() {
    this.subscribeNetworkServiceData();
    this.subscribeEvents();
    this.loadTranslation();
    this.subscribeTranslationEvent();
  }

  ngOnDestroy(): void {
    this._networkServiceSubscription?.unsubscribe();
    this.unsubscribeEvents();
    this._translateSubscription?.unsubscribe();
  }

  //#region Translation
  public loadTranslation(): void {
    this._translateService.get([
      "dictionary.addressType",
      "dictionary.mainGroup",
      "dictionary.mainValue",
      "dictionary.middleGroup",
      "dictionary.subGroup",
      "dictionary.afString",
      "dictionary.afKNXTwoLevels",
      "dictionary.afKNXThreeLevels",
      "dictionary.afInteger",
      "dictionary.afStringIntegerInteger",
      "dictionary.afStringStringInteger",
      "dictionary.afStringStringString",
      "dictionary.afIntegerStringString",
      "dictionary.afIntegerIntegerString",
      "dictionary.afIntegerStringInteger",
      "dictionary.afStringIntegerString",
      "dictionary.afStringString",
      "dictionary.afIntegerString",
      "dictionary.afStringInteger",
    ])
      .subscribe((translation) => {
        this.addressType = translation["dictionary.addressType"];
        this.maingroup = translation["dictionary.mainGroup"];
        this.mainValue = translation["dictionary.mainValue"];
        this.middlegroup = translation["dictionary.middleGroup"];
        this.subgroup = translation["dictionary.subGroup"];
        this.afString = translation["dictionary.afString"];
        this.afKNXTwoLevels = translation["dictionary.afKNXTwoLevels"];
        this.afKNXThreeLevels = translation["dictionary.afKNXThreeLevels"];
        this.afInteger = translation["dictionary.afInteger"];
        this.afStringIntegerInteger = translation["dictionary.afStringIntegerInteger"];
        this.afStringStringInteger = translation["dictionary.afStringStringInteger"];
        this.afStringStringString = translation["dictionary.afStringStringString"];
        this.afIntegerStringString = translation["dictionary.afIntegerStringString"];
        this.afIntegerIntegerString = translation["dictionary.afIntegerIntegerString"];
        this.afIntegerStringInteger = translation["dictionary.afIntegerStringInteger"];
        this.afStringIntegerString = translation["dictionary.afStringIntegerString"];
        this.afStringString = translation["dictionary.afStringString"];
        this.afIntegerString = translation["dictionary.afIntegerString"];
        this.afStringInteger = translation["dictionary.afStringInteger"];
      });
  }

  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalCtrl.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Each time an event arrives, it modifies the rows array, used by the table.
   * @param event ionChange event
   * @param id id of the row to be modified
   * @param input value ranging from 1 to 6 and indicates the six editable columns of the table
   * @param type indicates the type of the ion-select
   */
  public onChangeSave(event: any, id: number, input: number, type: number) {
    let value = event.detail.value;
    if (type == this.addressOperation.AddSubtract) {
      if (String(value).includes(".")) {
        let split = String(value).split(".");
        if (Number(split[1]) == 0) {
          value = split[0];
        } else {
          split[1] = "0";
          value = split.join(".");
        }
      } else {
        value = Math.round(value);
      }
    }
    switch (input) {
      case 1:
        this.rows[id].maingroup = value ??= this.rows[id].maingroup;
        if (this.rows[id].maingroup != this.addressOperation.DoNotModify) {
          let number = Number(this.rows[id].mainValue)
          this.rows[id].mainValue = number ? number : 0;
        }
        break;
      case 2:
        this.rows[id].mainValue = value ??= this.rows[id].mainValue;
        break;
      case 3:
        this.rows[id].middlegroup = value ??= this.rows[id].middlegroup;
        if (this.rows[id].middlegroup != this.addressOperation.DoNotModify) {
          let number = Number(this.rows[id].middleValue)
          this.rows[id].middleValue = number ? number : 0;
        }
        break;
      case 4:
        this.rows[id].middleValue = value ??= this.rows[id].middleValue;
        break;
      case 5:
        this.rows[id].subgroup = value ??= this.rows[id].subgroup;
        if (this.rows[id].subgroup != this.addressOperation.DoNotModify) {
          let number = Number(this.rows[id].subValue)
          this.rows[id].subValue = number ? number : 0;
        }
        break;
      case 6:
        this.rows[id].subValue = value ??= this.rows[id].subValue;
        break;
    }
  }

  /**
   * Returns the string with the text corresponding to the given id.
   */
  public getTextById(value: number): string {
    switch (value) {
      case AddressFormat.AfString:
        return this.afString;
      case AddressFormat.AfKNXTwoLevels:
        return this.afKNXTwoLevels;
      case AddressFormat.AfKNXThreeLevels:
        return this.afKNXThreeLevels;
      case AddressFormat.AfInteger:
        return this.afInteger;
      case AddressFormat.AfStringIntegerInteger:
        return this.afStringIntegerInteger;
      case AddressFormat.AfStringStringInteger:
        return this.afStringStringInteger;
      case AddressFormat.AfStringStringString:
        return this.afStringStringString;
      case AddressFormat.AfIntegerStringString:
        return this.afIntegerStringString;
      case AddressFormat.AfIntegerIntegerString:
        return this.afIntegerIntegerString;
      case AddressFormat.AfIntegerStringInteger:
        return this.afIntegerStringInteger;
      case AddressFormat.AfStringIntegerString:
        return this.afStringIntegerString;
      case AddressFormat.AfStringString:
        return this.afStringString;
      case AddressFormat.AfIntegerString:
        return this.afIntegerString;
      case AddressFormat.AfStringInteger:
        return this.afStringInteger;
    }
  }

  /**
   * Exit without press confirm.
   */
  public onClickCancel() {
    this._modalCtrl.dismiss();
  }

  /**
   * When clicked, it sends data for duplication to the component that generated it.
   */
  public async onClickDuplicate() {
    let copies = !this.duplicateForm.get('total').value || this.duplicateForm.get('total').value == 0 ? 1 : Math.abs(Math.round(this.duplicateForm.get('total').value));
    this.duplicateForm.patchValue(
      {
        total: copies,
      }
    );
    this._modalCtrl.dismiss({ copies: copies, methodString: this.createChangeMethodString() });
  }

  /**
   * Sets the values of the last two columns of the table
   * @param id id of the row to be set
   * @param type indicates which of the two columns to modify
   * 
   */
  private setSub(id: number, type: string) {
    switch (id) {
      case AddressFormat.AfString:
      case AddressFormat.AfStringStringString:
      case AddressFormat.AfIntegerStringString:
      case AddressFormat.AfIntegerIntegerString:
      case AddressFormat.AfStringIntegerString:
      case AddressFormat.AfStringString:
      case AddressFormat.AfIntegerString:
        return type == "group" ? this.addressOperation.DoNotModify : "";
      case AddressFormat.AfKNXTwoLevels:
      case AddressFormat.AfKNXThreeLevels:
      case AddressFormat.AfInteger:
      case AddressFormat.AfStringIntegerInteger:
      case AddressFormat.AfStringStringInteger:
      case AddressFormat.AfIntegerStringInteger:
      case AddressFormat.AfStringInteger:
        return type == "group" ? this.addressOperation.AddSubtract : 1;
    }
  }

  /**
   * Hides an entry of the indicated ion-select.
   * @param id id of the row where the ion-select is present
   * @param select value between 1 and 3 inclusive, used to distinguish the 3 ion-select present in each row
   * 
   */
  private showAllSelect(id: number, select: number): boolean {
    switch (id) {
      case AddressFormat.AfString:
      case AddressFormat.AfIntegerIntegerString:
      case AddressFormat.AfIntegerString:
        return select != 3;
      case AddressFormat.AfStringIntegerInteger:
        return select != 1;
      case AddressFormat.AfStringStringInteger:
        return select != 1 && select != 2;
      case AddressFormat.AfStringStringString:
        return false;
      case AddressFormat.AfIntegerStringString:
      case AddressFormat.AfStringString:
        return select != 3 && select != 2;
      case AddressFormat.AfIntegerStringInteger:
      case AddressFormat.AfStringInteger:
        return select != 2;
      case AddressFormat.AfStringIntegerString:
        return select != 1 && select != 3;
      default:
        return true;
    }
  }

  /**
   * Enable / Disable the indicated ion-select.
   * @param id id of the row where the ion-select is present
   * @param which it can be 1 or 2 and indicates the ion-select to be enabled / disabled
   * 
   */
  private blockInput(id: number, which: number): boolean {
    switch (id) {
      case AddressFormat.AfString:
      case AddressFormat.AfInteger:
        return true;
      case AddressFormat.AfKNXTwoLevels:
      case AddressFormat.AfStringString:
      case AddressFormat.AfIntegerString:
      case AddressFormat.AfStringInteger:
        return which == 1;
      default:
        return false;
    }
  }

  /**
   * Create the string necessary to modify the addresses of 
   * the new control groups and their possible controls.   
   */
  private createChangeMethodString(): string {
    let result: string = "";
    const initialSeparator: string = "$xx$";
    const separator: string = "$&$";
    const finalSeparator: string = "$#$";
    for (let i = 0; i < this.rows.length; i++) {
      switch (this.rows[i].id) {
        case AddressFormat.AfString:
        case AddressFormat.AfInteger:
          result = result + this.rows[i].id + initialSeparator + this.rows[i].subgroup + separator +
            this.rows[i].subValue + separator + 0 + separator + 0 + separator + 0 + separator + 0 + finalSeparator;
          break;
        case AddressFormat.AfKNXTwoLevels:
        case AddressFormat.AfStringString:
        case AddressFormat.AfIntegerString:
        case AddressFormat.AfStringInteger:
          result = result + this.rows[i].id + initialSeparator + this.rows[i].middlegroup + separator +
            this.rows[i].middleValue + separator + this.rows[i].subgroup + separator +
            this.rows[i].subValue + separator + 0 + separator + 0 + finalSeparator;
          break;
        case AddressFormat.AfKNXThreeLevels:
        case AddressFormat.AfStringIntegerInteger:
        case AddressFormat.AfStringStringInteger:
        case AddressFormat.AfStringStringString:
        case AddressFormat.AfIntegerStringString:
        case AddressFormat.AfIntegerIntegerString:
        case AddressFormat.AfIntegerStringInteger:
        case AddressFormat.AfStringIntegerString:
          result = result + this.rows[i].id + initialSeparator + this.rows[i].maingroup + separator +
            this.rows[i].mainValue + separator + this.rows[i].middlegroup + separator +
            this.rows[i].middleValue + separator + this.rows[i].subgroup + separator + this.rows[i].subValue + finalSeparator;
          break;
      }
    }
    return result.substring(0, result.length - 1);
  }

  /**
   * Initialize the array rows.
   */
  private initializeRows() {
    let enumArray = EnumStringConverter.genericEnumtoArray(AddressFormat);
    for (let i = 0; i < enumArray.length; i++) {
      this.rows.push(
        {
          id: enumArray[i],
          maingroup: this.addressOperation.DoNotModify,
          mainValue: '',
          middlegroup: this.addressOperation.DoNotModify,
          middleValue: '',
          subgroup: this.setSub(Number(enumArray[i]), "group"),
          subValue: this.setSub(Number(enumArray[i]), "value"),
          blockMain: this.blockInput(Number(enumArray[i]), 1),
          blockMiddle: this.blockInput(Number(enumArray[i]), 2),
          showAllSelectMain: this.showAllSelect(Number(enumArray[i]), 1),
          showAllSelectMiddle: this.showAllSelect(Number(enumArray[i]), 2),
          showAllSelectSub: this.showAllSelect(Number(enumArray[i]), 3),
        }
      );
    }
  }

  private initializeForm(): void {
    this.duplicateForm = this._formBuilder.group({
      total: new UntypedFormControl(1),
      duplicateCtrls: new UntypedFormControl(false),
    });
  }

  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );
        }
      );
  }
}
